export const FREE_PLAN_LIMIT = 30;

export const EXTRA_NEW_WORDS_STEP = 30;

const WEB_PLAY_MODES = ["multiple_choice", "flashcard", "text_input", "full_text_input"];

const findCollection = (collections, collectionId) => collections.find((c) => String(c.id) === String(collectionId));

const isReviewOn = (cc) => cc.reviewEnabled ?? cc.enabled;

const isNewWordsOn = (cc) => cc.newWordsEnabled ?? cc.enabled;

const isPresent = (value) => value !== null && value !== undefined;

export function todayString(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function resolvePlayStyle(skill, mode, available) {
  const unavailable = (skill === "listening" && !available.listening) || (skill === "speaking" && !available.speaking);
  if(!unavailable) {
    const isVocabularyOnlyMode = mode === "flashcard" || mode === "sentence_builder";
    return { mode: skill !== "vocabulary" && isVocabularyOnlyMode ? "multiple_choice" : mode, skill };
  }
  return { mode: mode === "full_text_input" ? "text_input" : mode, skill: "vocabulary" };
}

export function reviewCapacity(collection) {
  const originalReady = collection.numReadyForReview + (collection.numReviewedToday || 0);
  return isPresent(collection.maxReviewsPerDay) ? Math.min(originalReady, collection.maxReviewsPerDay) : originalReady;
}

export function newWordsCapacity(collection) {
  return Math.max(0, collection.numSentences - collection.numPlaying) + (collection.numNewToday || 0);
}

function capToFreeLimit(queue) {
  let remaining = FREE_PLAN_LIMIT;
  const capped = [];
  for(const item of queue) {
    if(remaining <= 0) break;
    const total = Math.min(item.total, remaining);
    capped.push({ ...item, completed: Math.min(item.completed, total), total });
    remaining -= total;
  }
  return capped;
}

export function buildQueue(collections, config, { isPro = true, isSpeakingAvailable = false, isSystemTtsAvailable = false } = {}) {
  const enabledConfigs = config.collections.filter((c) => c.enabled);
  const reviewSorted = [...enabledConfigs].sort((a, b) => a.reviewOrder - b.reviewOrder);
  const newWordsSorted = [...enabledConfigs].sort((a, b) => a.newWordsOrder - b.newWordsOrder);
  const playStyleFor = (collection, skill, mode) => resolvePlayStyle(skill, mode, {
    listening: !!(isSystemTtsAvailable || collection.ttsAvailable),
    speaking: isSpeakingAvailable
  });

  const reviewItems = [];
  for(const cc of reviewSorted) {
    const collection = findCollection(collections, cc.collectionId);
    if(!collection || !collection.dashboardCollection || collection.numPlaying === 0 || !isReviewOn(cc)) continue;

    const reviewedToday = collection.numReviewedToday || 0;
    const capacity = reviewCapacity(collection);
    const total = isPresent(cc.reviewsPerDay) ? Math.min(capacity, cc.reviewsPerDay) : capacity;
    if(total <= 0) continue;

    const completed = Math.min(reviewedToday, total);
    const remaining = Math.min(total - completed, collection.numReadyForReview);
    const configuredRoundLength = cc.reviewRoundLength ?? 10;
    reviewItems.push({
      collectionId: String(collection.id),
      collectionName: collection.name,
      completed,
      roundLength: remaining > 0 ? Math.min(configuredRoundLength, remaining) : configuredRoundLength,
      total,
      type: "review",
      ...playStyleFor(collection, cc.reviewSkill, cc.reviewMode)
    });
  }

  const newWordItems = [];
  for(const cc of newWordsSorted) {
    const collection = findCollection(collections, cc.collectionId);
    if(!collection || !collection.dashboardCollection || !isNewWordsOn(cc)) continue;

    const planned = Math.min(cc.newWordsPerDay, collection.numSentences - collection.numPlaying + (collection.numNewToday || 0));
    if(planned <= 0) continue;

    const completed = Math.min(collection.numNewToday || 0, planned);
    const remaining = planned - completed;
    const configuredRoundLength = cc.newWordsRoundLength ?? cc.newWordsPerDay;
    newWordItems.push({
      collectionId: String(collection.id),
      collectionName: collection.name,
      completed,
      roundLength: remaining > 0 ? Math.min(configuredRoundLength, remaining) : configuredRoundLength,
      total: planned,
      type: "new_words",
      ...playStyleFor(collection, cc.newWordsSkill, cc.newWordsMode)
    });
  }

  const queue = config.newWordsFirst ? [...newWordItems, ...reviewItems] : [...reviewItems, ...newWordItems];
  return isPro ? queue : capToFreeLimit(queue);
}

export function resolveConfig(collections, playOptions, savedConfig) {
  const defaultConfig = buildDefaultConfig(collections, playOptions);
  return savedConfig ? mergeConfig(defaultConfig, savedConfig) : defaultConfig;
}

export function buildDefaultConfig(collections, playOptions) {
  const skill = (playOptions && playOptions.skill) || "vocabulary";
  const mode = (playOptions && playOptions.mode) || "multiple_choice";

  return {
    collections: collections.filter((c) => c.dashboardCollection).map((c, i) => ({
      collectionId: String(c.id),
      enabled: true,
      newWordsEnabled: true,
      newWordsMode: mode,
      newWordsOrder: i,
      newWordsPerDay: 10,
      newWordsRoundLength: 10,
      newWordsSkill: skill,
      order: i,
      reviewEnabled: true,
      reviewMode: mode,
      reviewOrder: i,
      reviewRoundLength: 10,
      reviewSkill: skill,
      reviewsPerDay: null
    })),
    date: todayString(),
    newWordsFirst: false
  };
}

export function mergeConfig(defaultConfig, savedConfig) {
  const merged = [];
  const usedIds = new Set();

  for(const saved of savedConfig.collections || []) {
    const defaults = defaultConfig.collections.find((d) => d.collectionId === saved.collectionId);
    if(defaults) {
      merged.push({
        ...defaults,
        ...saved,
        newWordsOrder: saved.newWordsOrder ?? saved.order,
        reviewOrder: saved.reviewOrder ?? saved.order
      });
      usedIds.add(saved.collectionId);
    }
  }

  for(const defaults of defaultConfig.collections) {
    if(!usedIds.has(defaults.collectionId)) {
      merged.push(defaults);
    }
  }

  return { ...defaultConfig, collections: merged, newWordsFirst: savedConfig.newWordsFirst ?? false };
}

function legacyCount(count, { unlimited, fallback }) {
  if(count === "infinite") return unlimited;
  return Number.isInteger(count) ? count : fallback;
}

function legacyPositions(collectionIds, legacyOrder) {
  const order = legacyOrder.map(String);
  const rank = (id) => order.includes(id) ? order.indexOf(id) : order.length;
  return [...collectionIds].sort((a, b) => rank(a) - rank(b));
}

// The first web Today's Plan kept its choices in this browser, keyed by "review-<id>" and "new-<id>".
export function migrateLegacyPlan(defaultConfig, collections, { newWordsOrder, options, reviewOrder, unchecked }) {
  const collectionIds = defaultConfig.collections.map((cc) => cc.collectionId);
  const legacyIds = [...Object.keys(unchecked), ...Object.keys(options)].map((key) => key.replace(/^(review|new)-/, ""));
  const hasLegacyChoices = [...legacyIds, ...reviewOrder, ...newWordsOrder].some((id) => collectionIds.includes(String(id)));
  if(!hasLegacyChoices) return null;

  const reviewPositions = legacyPositions(collectionIds, reviewOrder);
  const newWordsPositions = legacyPositions(collectionIds, newWordsOrder);
  return {
    ...defaultConfig,
    collections: defaultConfig.collections.map((cc) => {
      const id = cc.collectionId;
      const reviewOptions = options[`review-${id}`];
      const newWordsOptions = options[`new-${id}`];
      const collection = findCollection(collections, id);
      return {
        ...cc,
        newWordsEnabled: !unchecked[`new-${id}`],
        newWordsOrder: newWordsPositions.indexOf(id),
        order: reviewPositions.indexOf(id),
        reviewEnabled: !unchecked[`review-${id}`],
        reviewOrder: reviewPositions.indexOf(id),
        // Sections never changed keep the app's defaults, so the app's plan does not change
        ...(reviewOptions ? {
          reviewMode: reviewOptions.mode,
          reviewSkill: reviewOptions.skill,
          reviewsPerDay: legacyCount(reviewOptions.count, { fallback: cc.reviewsPerDay, unlimited: null })
        } : {}),
        ...(newWordsOptions ? {
          newWordsMode: newWordsOptions.mode,
          newWordsPerDay: legacyCount(newWordsOptions.count, { fallback: cc.newWordsPerDay, unlimited: newWordsCapacity(collection) }),
          newWordsSkill: newWordsOptions.skill
        } : {})
      };
    })
  };
}

// Even split; remainders favor earlier entries, overflow rolls to entries with room.
function allocate(total, capacities) {
  const out = capacities.map(() => 0);
  let remaining = Math.min(total, capacities.reduce((s, c) => s + c, 0));
  while(remaining > 0) {
    const active = capacities.map((c, i) => i).filter((i) => out[i] < capacities[i]);
    if(!active.length) break;
    const base = Math.max(1, Math.floor(remaining / active.length));
    for(const i of active) {
      if(remaining <= 0) break;
      const give = Math.min(base, capacities[i] - out[i], remaining);
      out[i] += give;
      remaining -= give;
    }
  }
  return out;
}

function distributeTotal(config, collections, total, { field, isOn, orderKey, capacityOf }) {
  const targets = [...config.collections]
    .sort((a, b) => a[orderKey] - b[orderKey])
    .filter((cc) => findCollection(collections, cc.collectionId) && isOn(cc));
  const allocated = allocate(total, targets.map((cc) => capacityOf(findCollection(collections, cc.collectionId))));
  const byId = new Map(targets.map((cc, i) => [cc.collectionId, allocated[i]]));
  return {
    ...config,
    collections: config.collections.map((cc) => byId.has(cc.collectionId) ? { ...cc, [field]: byId.get(cc.collectionId) } : cc)
  };
}

export function distributeNewWordsTotal(config, collections, total) {
  return distributeTotal(config, collections, total, {
    capacityOf: newWordsCapacity,
    field: "newWordsPerDay",
    isOn: isNewWordsOn,
    orderKey: "newWordsOrder"
  });
}

export function distributeReviewsTotal(config, collections, total) {
  return distributeTotal(config, collections, total, {
    capacityOf: reviewCapacity,
    field: "reviewsPerDay",
    isOn: isReviewOn,
    orderKey: "reviewOrder"
  });
}

export function newWordsLeftBeyondPlan(collections, queue) {
  const unplayed = collections.filter((c) => c.dashboardCollection).reduce((s, c) => s + Math.max(0, c.numSentences - c.numPlaying), 0);
  const stillPlanned = queue.filter((i) => i.type === "new_words").reduce((s, i) => s + i.total - i.completed, 0);
  return unplayed - stillPlanned;
}

// +30 always adds 30: when the collections with new words on don't have enough unplayed words left, new words go on for
// the next ones that do. Counts unplayed words only, since words already played today can't be played again.
export function turnOnNewWordsToFit(config, collections, wordsToPlay) {
  const capacityOf = (cc) => {
    const collection = findCollection(collections, cc.collectionId);
    return collection && collection.dashboardCollection ? Math.max(0, collection.numSentences - collection.numPlaying) : 0;
  };
  let capacity = config.collections.filter(isNewWordsOn).reduce((s, cc) => s + capacityOf(cc), 0);
  const turnedOn = new Set();
  for(const cc of [...config.collections].sort((a, b) => a.newWordsOrder - b.newWordsOrder)) {
    if(capacity >= wordsToPlay) break;
    if(isNewWordsOn(cc) || capacityOf(cc) <= 0) continue;
    turnedOn.add(cc.collectionId);
    capacity += capacityOf(cc);
  }
  if(!turnedOn.size) return config;
  return {
    ...config,
    collections: config.collections.map((cc) => turnedOn.has(cc.collectionId) ? withSectionFlag(cc, "new_words", true) : cc)
  };
}

export function addExtraNewWords(config, collections, extra) {
  if(extra <= 0) return config;
  const planned = config.collections.filter(isNewWordsOn).reduce((s, cc) => s + cc.newWordsPerDay, 0);
  return distributeNewWordsTotal(config, collections, planned + extra);
}

export function buildPlanRows(config, collections, queue) {
  const rowFor = (collection, type, { enabled, perDay, capacity }) => {
    const item = queue.find((i) => i.collectionId === String(collection.id) && i.type === type);
    return {
      capacity,
      collectionId: String(collection.id),
      completed: item ? item.completed : 0,
      enabled: !!enabled,
      name: collection.name,
      perDay,
      total: item ? item.total : 0,
      type
    };
  };

  const reviewRows = [];
  for(const cc of [...config.collections].sort((a, b) => a.reviewOrder - b.reviewOrder)) {
    const collection = findCollection(collections, cc.collectionId);
    if(!collection || collection.numReadyForReview + (collection.numReviewedToday || 0) <= 0) continue;
    const capacity = reviewCapacity(collection);
    const perDay = isPresent(cc.reviewsPerDay) ? Math.min(capacity, cc.reviewsPerDay) : capacity;
    reviewRows.push(rowFor(collection, "review", { capacity, enabled: isReviewOn(cc), perDay }));
  }

  const newWordsRows = [];
  for(const cc of [...config.collections].sort((a, b) => a.newWordsOrder - b.newWordsOrder)) {
    const collection = findCollection(collections, cc.collectionId);
    if(!collection || newWordsCapacity(collection) <= 0) continue;
    const capacity = Math.max(0, collection.numSentences - collection.numPlaying);
    newWordsRows.push(rowFor(collection, "new_words", { capacity, enabled: isNewWordsOn(cc), perDay: cc.newWordsPerDay }));
  }

  return { newWordsRows, reviewRows };
}

const sectionFlag = (type) => type === "review" ? "reviewEnabled" : "newWordsEnabled";

function withSectionFlag(cc, type, value) {
  const updated = { ...cc, [sectionFlag(type)]: value };
  return { ...updated, enabled: !!(isReviewOn(updated) || isNewWordsOn(updated)) };
}

export function toggleCollection(config, collectionId, type) {
  return {
    ...config,
    collections: config.collections.map((cc) => {
      if(cc.collectionId !== collectionId) return cc;
      return withSectionFlag(cc, type, !(type === "review" ? isReviewOn(cc) : isNewWordsOn(cc)));
    })
  };
}

export function toggleSection(config, collectionIds, type, value) {
  return {
    ...config,
    collections: config.collections.map((cc) => collectionIds.includes(cc.collectionId) ? withSectionFlag(cc, type, value) : cc)
  };
}

export function reorderSection(config, collectionIds, type, index, direction) {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if(targetIndex < 0 || targetIndex >= collectionIds.length) return config;

  const swappedIds = [...collectionIds];
  [swappedIds[index], swappedIds[targetIndex]] = [swappedIds[targetIndex], swappedIds[index]];
  const orderKey = type === "review" ? "reviewOrder" : "newWordsOrder";
  // Every collection is renumbered, so one hidden today cannot share a position with a shown one.
  let nextSwapped = 0;
  const ids = [...config.collections]
    .sort((a, b) => a[orderKey] - b[orderKey])
    .map((cc) => collectionIds.includes(cc.collectionId) ? swappedIds[nextSwapped++] : cc.collectionId);
  return {
    ...config,
    collections: config.collections.map((cc) => {
      const position = ids.indexOf(cc.collectionId);
      if(position === -1) return cc;
      return type === "review" ? { ...cc, order: position, reviewOrder: position } : { ...cc, [orderKey]: position };
    })
  };
}

export function setCollectionPlayOptions(config, collectionId, updates) {
  return {
    ...config,
    collections: config.collections.map((cc) => cc.collectionId === collectionId ? { ...cc, ...updates } : cc)
  };
}

export function freeCardsRemainingToday(collections) {
  const playedToday = collections.reduce((s, c) => s + (c.numPlayedToday || 0), 0);
  return Math.max(0, FREE_PLAN_LIMIT - playedToday);
}

export function untappedReviews(collections, queue) {
  const totalReady = collections.reduce((s, c) => s + (c.numReadyForReview || 0), 0);
  const remainingInPlan = queue.filter((i) => i.type === "review").reduce((s, i) => s + (i.total - i.completed), 0);
  return Math.max(0, totalReady - remainingInPlan);
}

export function reviewsBeyondFreeLimit(collections, config, freeQueue, options = {}) {
  const reviewTotal = (queue) => queue.filter((i) => i.type === "review").reduce((s, i) => s + i.total, 0);
  return Math.max(0, reviewTotal(buildQueue(collections, config, { ...options, isPro: true })) - reviewTotal(freeQueue));
}

export function isDailyLimitReached(collections, isPro) {
  return !isPro && freeCardsRemainingToday(collections) === 0;
}

export function isComplete(queue) {
  return queue.length > 0 && queue.every((item) => item.completed >= item.total);
}

export function nextInQueue(queue) {
  return queue.find((item) => item.completed < item.total) || null;
}

export function reviewAheadItem(collections, config, { isSpeakingAvailable = false, isSystemTtsAvailable = false } = {}) {
  for(const cc of [...config.collections].sort((a, b) => a.reviewOrder - b.reviewOrder)) {
    const collection = findCollection(collections, cc.collectionId);
    if(!collection || !collection.dashboardCollection || collection.numPlaying === 0) continue;
    return {
      collectionId: String(collection.id),
      collectionName: collection.name,
      roundLength: cc.reviewRoundLength ?? 10,
      type: "review_ahead",
      ...resolvePlayStyle(cc.reviewSkill, cc.reviewMode, {
        listening: !!(isSystemTtsAvailable || collection.ttsAvailable),
        speaking: isSpeakingAvailable
      })
    };
  }
  return null;
}

export function classifyUserType(firstViewedDate, today) {
  return firstViewedDate && firstViewedDate < today ? "existing" : "new";
}

// Mobile-saved settings can hold modes the web lacks; Translate is review-only behind its experiment.
export function webPlayMode(item, isTranslateReviewsEnabled) {
  if(!WEB_PLAY_MODES.includes(item.mode)) return "multiple_choice";
  const isTranslate = item.mode === "full_text_input" && item.skill === "vocabulary";
  if(isTranslate && !(isTranslateReviewsEnabled && item.type !== "new_words")) return "text_input";
  return item.mode;
}

export function buildPlayUrl(collection, item, { isTranslateReviewsEnabled = false } = {}) {
  const params = new URLSearchParams();
  params.set("skill", item.skill);
  params.set("mode", webPlayMode(item, isTranslateReviewsEnabled));
  params.set("count", item.roundLength);
  // No scope when reviewing ahead: the server fills the round with reviews due later
  if(item.type !== "review_ahead") params.set("scope", item.type === "review" ? "ready_for_review" : "new");
  if(item.type !== "review_ahead") params.set("todays_plan", "1");
  if(isTranslateReviewsEnabled) params.set("translate_reviews", "1");
  return collection.playWebUrl + "?" + params.toString();
}

export function isEligibleForAnnouncement(firstViewedDate, accountCreatedAt, today) {
  const hasUsedTodaysPlanBefore = classifyUserType(firstViewedDate, today) === "existing";
  return !hasUsedTodaysPlanBefore && todayString(new Date(accountCreatedAt)) < today;
}
